import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { AnfrageStatus, AnfrageTyp, SlaStatus } from '../lib/api-enums';
import { formatGermanDateTime, msUntil } from '../lib/date-format';

interface Anfrage {
  id: string;
  eingangAm: string;
  quelle: string;
  betreff: string | null;
  anfrageTyp: AnfrageTyp | null;
  kundeId: string | null;
  status: AnfrageStatus;
  slaReaktionBis: string | null;
  slaStatus: SlaStatus | null;
  zugeordnetAnUserId: string | null;
}

const STATUS_LABELS: Record<AnfrageStatus, string> = {
  neu: 'Neu',
  in_triage: 'In Triage',
  zugeordnet: 'Zugeordnet',
  konvertiert: 'Konvertiert',
  geschlossen: 'Geschlossen',
  dupliziert: 'Dupliziert',
};

const STATUS_COLORS: Record<AnfrageStatus, string> = {
  neu: 'bg-amber-100 text-amber-800 border-amber-200',
  in_triage: 'bg-blue-100 text-blue-800 border-blue-200',
  zugeordnet: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  konvertiert: 'bg-green-100 text-green-800 border-green-200',
  geschlossen: 'bg-slate-100 text-slate-600 border-slate-200',
  dupliziert: 'bg-slate-100 text-slate-500 border-slate-200',
};

const TYP_LABELS: Record<AnfrageTyp, string> = {
  reparatur: 'Reparatur',
  wartung: 'Wartung',
  notdienst: 'Notdienst',
  reklamation: 'Reklamation',
  beratung: 'Beratung',
  aufmass: 'Aufmaß',
  sonstige: 'Sonstige',
};

const QUELLE_LABELS: Record<string, string> = {
  email: '✉️ Mail',
  portal: '🌐 Portal',
  telefon: '📞 Telefon',
  caretaker: '🔧 Hausmeister',
  sonstige: '— sonst.',
};

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/inbox/')({
  beforeLoad: () => requireAccess('/api/v1/inbox'),
  component: InboxListPage,
});

function InboxListPage() {
  const [statusFilter, setStatusFilter] = useState<AnfrageStatus | ''>('');

  const inboxQ = useQuery({
    queryKey: ['inbox', statusFilter || 'all'],
    queryFn: () =>
      api<{ items: Anfrage[] }>(
        statusFilter ? `/api/v1/inbox?status=${statusFilter}` : '/api/v1/inbox',
      ),
  });

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const kundenById = new Map((kundenQ.data?.items ?? []).map((k) => [k.id, k]));

  const items = inboxQ.data?.items ?? [];
  const counts = items.reduce<Record<string, number>>((acc, a) => {
    acc[a.status] = (acc[a.status] ?? 0) + 1;
    if (a.slaStatus === 'verletzt') acc['__sla_verletzt__'] = (acc['__sla_verletzt__'] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inbox"
        description="Eingehende Anfragen aus Mail / Telefon / Caretaker. Triage + SLA-Status."
        actions={
          <Link
            to="/inbox/neu"
            className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            + Manuelle Anfrage
          </Link>
        }
      />

      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-slate-700">Filter:</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AnfrageStatus | '')}
            className="w-44"
          >
            <option value="">Alle Status</option>
            {(Object.keys(STATUS_LABELS) as AnfrageStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]} ({counts[s] ?? 0})
              </option>
            ))}
          </Select>
          {counts['__sla_verletzt__'] && counts['__sla_verletzt__'] > 0 ? (
            <span className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700">
              ⚠ {counts['__sla_verletzt__']} SLA verletzt
            </span>
          ) : null}
        </div>
      </div>

      {inboxQ.isError ? <ErrorBanner error={inboxQ.error} /> : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 text-xs font-medium text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Eingang</th>
              <th className="px-3 py-2 text-left">Quelle</th>
              <th className="px-3 py-2 text-left">Betreff</th>
              <th className="px-3 py-2 text-left">Kunde</th>
              <th className="px-3 py-2 text-left">Typ</th>
              <th className="px-3 py-2 text-left">SLA</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {items.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-center text-slate-500" colSpan={7}>
                  {inboxQ.isLoading ? 'lädt …' : 'Keine Anfragen.'}
                </td>
              </tr>
            ) : (
              items.map((a) => {
                const kunde = a.kundeId ? kundenById.get(a.kundeId) : undefined;
                return (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">
                      <Link
                        to="/inbox/$id"
                        params={{ id: a.id }}
                        className="text-brand-700 hover:underline"
                      >
                        {formatGermanDateTime(a.eingangAm)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {QUELLE_LABELS[a.quelle] ?? a.quelle}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to="/inbox/$id"
                        params={{ id: a.id }}
                        className="text-slate-900 hover:underline"
                      >
                        {a.betreff ?? <span className="italic text-slate-400">— kein Betreff</span>}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {kunde ? (
                        `${kunde.kundennummer} — ${kunde.firmenname}`
                      ) : a.kundeId ? (
                        <span className="italic text-slate-400">unbekannt</span>
                      ) : (
                        <span className="italic text-slate-400">offen</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {a.anfrageTyp ? (
                        TYP_LABELS[a.anfrageTyp]
                      ) : (
                        <span className="italic text-slate-400">offen</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <SlaAmpel anfrage={a} />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status]}`}
                      >
                        {STATUS_LABELS[a.status]}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * SLA-Ampel: gruen wenn erfuellt oder noch viel Zeit, gelb wenn knapp,
 * rot wenn verletzt. Berechnet relativ zu now() falls noch offen.
 */
function SlaAmpel({ anfrage }: { anfrage: Anfrage }): JSX.Element {
  if (!anfrage.slaStatus || !anfrage.slaReaktionBis) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  if (anfrage.slaStatus === 'verletzt') {
    return <span className="text-sm font-bold text-red-600">●</span>;
  }
  if (anfrage.slaStatus === 'erfuellt') {
    return <span className="text-sm text-green-600">●</span>;
  }
  // offen — Zeit bis Frist berechnen
  const restMs = msUntil(anfrage.slaReaktionBis);
  const restMin = Math.floor(restMs / 60000);
  const totalMs =
    new Date(anfrage.slaReaktionBis).getTime() - new Date(anfrage.eingangAm).getTime();
  const ratio = totalMs > 0 ? restMs / totalMs : 0;
  if (restMs <= 0) {
    return (
      <span className="text-sm font-bold text-red-600" title="ueberfaellig">
        ●
      </span>
    );
  }
  const color = ratio < 0.25 ? 'text-amber-600' : 'text-green-600';
  return (
    <span className={`text-xs ${color}`} title={`noch ${restMin} min`}>
      ● {restMin > 60 ? `${Math.floor(restMin / 60)}h` : `${restMin}min`}
    </span>
  );
}
